import { z } from 'zod';

import type { WarehouseDocSpec } from './lib/doc-spec-schema.js';
import type { PostgresSslMode } from './lib/postgres-ssl-mode.js';
import type { RudderstackWorkspaceId } from './lib/rudderstack-id.js';
import { AccountRelinkSnapshotSchema } from './lib/warehouse-repair/types.js';
import { parseCron } from './lib/workflow/trigger-window.js';

// ─── Webhook types (existing) ───

export const PushPayloadSchema = z.object({
  ref: z.string(),
  after: z.string(),
  repository: z.object({
    name: z.string(),
    full_name: z.string(),
    default_branch: z.string(),
    owner: z.object({ login: z.string() }),
  }),
  installation: z.object({ id: z.number() }).optional(),
  commits: z
    .array(
      z.object({
        id: z.string(),
        message: z.string(),
        added: z.array(z.string()),
        modified: z.array(z.string()),
        removed: z.array(z.string()),
      }),
    )
    .optional(),
});

/**
 * Credentials for the RudderStack MCP. The two fields are only useful
 * together — construct via the validating guard in `addRudderstackMcp` (or
 * equivalent), so a half-populated pair never reaches the wire.
 */
export type RudderstackCredentials = {
  workspaceId: RudderstackWorkspaceId;
  pat: string;
};

// ─── User types (new) ───

export type AuthProvider = 'github' | 'rudderstack' | 'clerk';

export type User = {
  id: string;
  githubLogin: string | null;
  githubName: string | null;
  githubAvatarUrl: string | null;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UserSettings = {
  userId: string;
  rudderstackWorkspaceId: RudderstackWorkspaceId | null;
  rudderstackPat: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FavoriteEntityType = 'dashboard';

export interface UserFavorite {
  entityId: string;
  createdAt: string;
}

/** Sanitized (credential-free) config from RudderStack Snowflake destination API. */
export interface SnowflakeRsConfig {
  account?: string;
  database?: string;
  warehouse?: string;
  namespace?: string;
  role?: string;
  user?: string;
}

/** Sanitized (credential-free) config from RudderStack BigQuery destination API. */
export interface BigQueryRsConfig {
  project?: string;
  location?: string;
  namespace?: string;
}

/** Sanitized (credential-free) config from RudderStack Databricks destination API. */
export interface DatabricksRsConfig {
  host?: string;
  path?: string;
  httpPath?: string;
  catalog?: string;
  namespace?: string;
}

/** Sanitized (credential-free) config from RudderStack Redshift destination API. */
export interface RedshiftRsConfig {
  host?: string;
  port?: string | number;
  database?: string;
  user?: string;
  username?: string;
  namespace?: string;
  /** RS destination `useIAMForAuth` (stored as "true"/"false" after sync). */
  useIAMForAuth?: string;
  useIamForAuth?: string;
  iamAuth?: string;
  iamAuthEnabled?: string;
  clusterId?: string;
  clusterIdentifier?: string;
  clusterRegion?: string;
  region?: string;
  iamRoleARNForAuth?: string;
  iamRoleArnForAuth?: string;
  iamRoleArn?: string;
}

export type Warehouse = {
  rudderDestinationId: string;
  workspaceId: string;
  name: string;
  type: string; // 'POSTGRES', 'RS', 'BQ', 'SNOWFLAKE', etc.
  config: SnowflakeRsConfig | RedshiftRsConfig | BigQueryRsConfig | DatabricksRsConfig | Record<string, string> | null;
  updatedAt: string;
  /** Derived bucket identity: `${type}|${normHost}|${normDb}`. Null for unsupported types or missing config. */
  accountKey: string | null;
  /** Destination-level schema name (from `namespace` config key). Null when not configured. */
  schemaName: string | null;
};

export type WarehouseConnection = {
  workspaceId: string;
  /**
   * Surrogate connection identity (phase 1.5). Stable across coordinate edits, unlike connectionKey.
   */
  id: string;
  /**
   * The resolved destination ID this connection maps to (fan-out by the resolution query). Empty
   * string when the connection resolves no destination — use `rudderDestinationId || id` wherever a
   * single addressable string is needed (decision D2).
   */
  rudderDestinationId: string;
  /** Destination display name when one resolves; null otherwise. */
  warehouseName: string | null;
  /** Primary key on warehouse_connections: `accountKey|` (connections carry no schema, decision L). */
  connectionKey: string;
  /** Bucket identity: `TYPE|normHost|normDb`. */
  accountKey: string;
  connectionString: string;
  type: string;
  updatedAt: string;
  /**
   * The rETL account this connection is bound to, or null when it was never bound (phase 1's
   * `bindRsAccountId`). Selected by the resolution read so a resolver can tell bound from unbound
   * without a second query — the phase-2 credential read branches on it before it mints anything,
   * and `lookout.account.read`'s `outcome=bound|unbound` is read straight off it.
   *
   * Null, never `''`: an empty string would be a link to nothing that the binding guard (which only
   * re-fires on NULL) could never repair.
   */
  rsAccountId: string | null;
  /** The denormalized, human-readable name of that account. A label; nothing branches on it. */
  rsAccountName: string | null;
  /** 'platform': the credential lives only in RudderStack (3b). Never falls back, never mirrored. */
  credentialSource: 'local' | 'platform';
  /** Phase 3c: the bound RudderStack account is gone; artifacts refuse with "reconnect". Absent = not selected. */
  pausedReason?: WarehousePausedReason | null;
  /**
   * Phase 3d: when an owner or admin bound this connection to a RudderStack account by hand — and
   * therefore **the proof that Lookout may read that account's secret**.
   *
   * A customer's own account carries no `lookout:` `externalId` and never gains one, because no
   * platform write happens on an adopt. The phase 2 ownership test (`classifyRsAccountForRead`)
   * would therefore refuse every adopted row as `not_lookout_owned`. This column is what that test
   * consults instead: a row in Lookout's own database, written under an advisory lock, naming the
   * user who chose the account and when. Verified against prod, where 863 of 893 warehouse accounts
   * carry no mark at all — an `externalId`-based test could never have admitted them.
   *
   * Optional, like {@link pausedReason}: absent means the caller's query did not select it. Absent
   * and null are the same answer — not adopted — so a fixture that forgets it gets the stricter test.
   */
  rsAccountAdoptedAt?: string | null;
  /**
   * ACT2-1031: the Lookout compute warehouse (Snowflake only). When set, Lookout dials this warehouse
   * instead of the one in the credential or account options; reverse ETL keeps the compute warehouse.
   * Lives in its own column, never in `connectionString`, so it cannot reach a platform payload.
   *
   * Optional like {@link pausedReason}: absent means the caller's query did not select it, which reads
   * as "no override". Every resolution read selects it.
   */
  lookoutWarehouse?: string | null;
};

/** Phase 3c: why a connection is paused until an admin reconnects it. */
export type WarehousePausedReason = 'account_deleted' | 'account_revoked';

export type SnowflakeConnectionConfig = {
  account: string;
  username: string;
  database: string;
  warehouse: string;
  schema?: string;
  role?: string;
  authType: 'password' | 'key_pair' | 'managed_key';
  password?: string;
  privateKey?: string;
  privateKeyPassphrase?: string;
};

/**
 * What the Snowflake form sends to `updateConnection` and `testConnection` (ACT2-1031): the stored
 * config plus the Lookout compute warehouse. The server takes `lookoutWarehouse` off before it builds
 * the credential blob — it is stored in its own column and never reaches RudderStack. Blank clears it.
 */
export type SnowflakeConnectionInput = SnowflakeConnectionConfig & { lookoutWarehouse?: string };

export type RedshiftSslMode = 'disable' | 'require' | 'verify-full';

export type RedshiftPasswordConnectionConfig = {
  authType: 'password' | 'managed_password';
  host: string;
  port: number;
  database: string;
  user: string;
  password?: string;
  schema?: string;
  sslmode?: RedshiftSslMode;
};

export type RedshiftIamConnectionConfig = {
  authType: 'iam';
  host: string;
  port: number;
  database: string;
  user: string;
  clusterIdentifier: string;
  clusterRegion: string;
  iamRoleArn: string;
  schema?: string;
  sslmode?: RedshiftSslMode;
};

export type RedshiftConnectionConfig = RedshiftPasswordConnectionConfig | RedshiftIamConnectionConfig;

export type { PostgresSslMode } from './lib/postgres-ssl-mode.js';

export type PostgresFormSslMode = Extract<PostgresSslMode, 'disable' | 'require'>;

export type PostgresConnectionConfig = {
  host: string;
  port: number;
  database: string;
  user: string;
  password?: string;
  sslmode?: PostgresSslMode;
  schema?: string;
};

export type BigQueryServiceAccountCredentials = {
  type?: string;
  project_id?: string;
  private_key_id?: string;
  private_key?: string;
  client_email: string;
  client_id?: string;
  auth_provider_x509_cert_url?: string;
  client_x509_cert_url?: string;
  universe_domain?: string;
};

export type BigQueryConnectionConfig = {
  projectId: string;
  /**
   * The project's BigQuery region, when anything knows it. OPTIONAL, because a `SOURCE_BIGQUERY`
   * account is not required to carry one and most do not.
   *
   * It is never sent as the JOB location: BigQuery infers that from the region qualifier in the SQL,
   * and an explicit location that disagrees with the qualifier fails a query that would otherwise
   * have run (verified live — `region-us` SQL with `location: 'EU'` fails hard, while the same SQL
   * with no location auto-selects correctly). Its one remaining use is the `region-{{REGION}}`
   * qualifier itself, which is why `bigquery-region-cache.ts` derives one when the account has none.
   */
  location?: string;
  /** Hard cap for bytes billed per query. BigQuery rejects over-limit jobs before execution. */
  maxBytesBilled?: string;
  credentials: BigQueryServiceAccountCredentials;
};

export type DatabricksConnectionConfig = {
  host: string;
  httpPath: string;
  catalog?: string;
  schema?: string;
  authType: 'pat' | 'oauth_m2m';
  token?: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
};

/**
 * The ClickHouse account shape (Lookout LLD 2.1). `ClickHouseConnectionConfig` below
 * keeps typing the legacy local shape until its last writer is gone.
 */
export type ClickHouseAccountConfig = {
  host: string;
  port: number;
  database: string;
  user: string;
  password: string;
  secure: true;
  skipVerify: false;
  rudderSchema?: string;
};

export type ClickHouseCoordinates = Omit<ClickHouseAccountConfig, 'password'>;

export type ClickHouseConnectionConfig = {
  host: string;
  port: number;
  database: string;
  user: string;
  password?: string;
  secure: boolean;
  cluster?: string;
};

/** Redacted version returned to the frontend (connection string masked) */
export type WarehouseConnectionRedacted = {
  /** Surrogate connection identity (phase 1.5); stable across coordinate edits. */
  id: string;
  rudderDestinationId: string;
  connectionKey: string;
  accountKey: string;
  /** Bound RudderStack account id (`warehouse_connections.rs_account_id`); null when unbound. */
  rsAccountId?: string | null;
  /** Bound RudderStack account name. Display only — never a log field; may be a Lookout-minted name. */
  rsAccountName?: string | null;
  connectionString: string | null; // redacted
  config?:
    | SnowflakeConnectionConfig
    | RedshiftConnectionConfig
    | PostgresConnectionConfig
    | BigQueryConnectionConfig
    | DatabricksConnectionConfig
    | ClickHouseConnectionConfig;
};

/**
 * ACT2-1109: what the settings list shows about a connection's background Warehouse orientation run.
 * The stored states plus `interrupted`, which the service derives for a run no owner is still working
 * on (the process stopped mid-run). Null when no background run was ever admitted for the connection.
 */
export type WarehouseOrientationDisplayStatus = 'queued' | 'running' | 'done' | 'failed' | 'blocked' | 'interrupted';
/**
 * ACT2-1340: the outcome's detail. `partial` with `done` (some reads were refused or timed out). With
 * `blocked`: `credential` (the warehouse rejected Lookout's sign-in), `credential_unavailable` (Lookout
 * could not produce a credential to sign in with: a RudderStack secret it could not fetch, an IAM
 * failure), `paused` (the connection is paused, so the run did not try), `no_reads` (every read failed
 * for another reason). Null for every other status.
 */
export type WarehouseOrientationReason = 'partial' | 'credential' | 'credential_unavailable' | 'paused' | 'no_reads';
export type WarehouseOrientationDisplay = {
  status: WarehouseOrientationDisplayStatus;
  reason: WarehouseOrientationReason | null;
  /** ISO time the status last changed. */
  updatedAt: string | null;
  /** The entry note the run writes (`<Engine> orientation`), for the list to point at; null when unknown. */
  noteTitle: string | null;
  /**
   * ACT2-1340: what went wrong and what to do, in plain words, for a `blocked` or partial run; null
   * otherwise. Filled by the service (`withOrientationDisplay`), from the warehouse failure copy.
   */
  fix: { message: string; nextStep: string } | null;
  /** ACT2-1340: the last run did not fully succeed, so an admin may run it again. */
  canRetry: boolean;
};

/** One stored connection in the settings list (`settings.warehouses.listAccounts`). The SPA derives its view model from it. */
export type WarehouseAccountRow = {
  /**
   * The connection's surrogate uuid (phase 1.5) — the id the public surface addresses it by, and the
   * one stable handle a reader can use to tell two connections apart when their display labels
   * collide (see `disambiguateConnectionNames`). `connectionKey` cannot serve: it embeds the host dim.
   */
  id: string;
  connectionKey: string;
  accountKey: string;
  type: string;
  /** Chosen compute warehouse pulled from the stored blob (config.warehouse / httpPath). */
  computeWarehouse: string | null;
  /** ACT2-1031: the Lookout compute warehouse override (Snowflake only); null when Lookout uses `computeWarehouse`. */
  lookoutWarehouse: string | null;
  /** Redacted connection config (secrets masked); null connectionString when parsed. */
  config?: WarehouseConnectionRedacted['config'];
  linkedDestinations: Array<{ rudderDestinationId: string; name: string }>;
  linkedCount: number;
  /** Linked modeling projects for warehouse documentation. */
  warehouseProjectCount: number;
  /** Bound RudderStack account id; null when unbound. */
  rsAccountId: string | null;
  /** Bound RudderStack account name. Display only — never a log field; may be a Lookout-minted name. */
  rsAccountName: string | null;
  /**
   * Where this connection's credential lives (phase 3b decision 4). Not a secret and not a
   * coordinate — it is what the settings screen needs to tell the admin that deleting a `platform`
   * connection leaves its RudderStack account, and the credential inside it, behind (decision D-a).
   */
  credentialSource: 'local' | 'platform';
  /** Phase 3c: set when the bound account is gone; the card offers Reconnect. */
  pausedReason: WarehousePausedReason | null;
  /** Phase 3c: which key a managed-key Snowflake connection signs with; null for every other connection. */
  keySource: 'platform' | 'workspace' | null;
  /** ACT2-1109: the background Warehouse orientation run for this connection; null when none was admitted. */
  orientation: WarehouseOrientationDisplay | null;
};

// ─── Chat types (new) ───

/**
 * Chat names. One cap for every writer: a user rename is REJECTED above it, and an agent
 * `change_title` is CLAMPED to it, because a model mistake should not discard the retitle.
 *
 * An auto-title obeys this cap too. Only its FALLBACK — the opening message used verbatim when the
 * model returns nothing — is cut shorter, by `fallbackTitle` in `src/agent/chat-titler.ts`.
 */
export const CHAT_TITLE_MAX = 200;

/**
 * Characters a chat title may not hold. Control characters (`\p{Cc}`): a NUL reaches Postgres, which
 * rejects it with `22021` before the ownership check even runs, and the untyped failure surfaces as a
 * 500. Bidi overrides and isolates, and the invisible spacers: they let one row in a shared chat list
 * pass for another, and the title is also that row's accessible name.
 *
 * The zero-width JOINER (U+200D) stays allowed: composite emoji such as 👨‍💻 are built from it.
 */
const TITLE_FORBIDDEN_CHARS = /[\p{Cc}\u200B\u200E\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/u;
// Same class, global, for stripping. A literal rather than `new RegExp(source)`, which the security lint
// cannot tell apart from a pattern built from input. Keep the two in step.
const TITLE_FORBIDDEN_CHARS_ALL = /[\p{Cc}\u200B\u200E\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/gu;

export const ChatTitleSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHAT_TITLE_MAX)
  .refine((title) => !TITLE_FORBIDDEN_CHARS.test(title), 'Chat title contains characters a name cannot hold');

/**
 * Bring an agent-supplied title within `CHAT_TITLE_MAX`. Returns `null` when nothing usable is left,
 * which the caller treats as "leave the current title alone" — an empty title would otherwise render
 * as a nameless row with an empty accessible name.
 */
export function clampChatTitle(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  // A model's title is clamped rather than rejected, so it is cleaned of the same characters the
  // schema refuses, instead of failing the write.
  const trimmed = raw.replace(TITLE_FORBIDDEN_CHARS_ALL, '').trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length <= CHAT_TITLE_MAX) return trimmed;
  const cut = cutAtCodePoint(trimmed, CHAT_TITLE_MAX).trimEnd();
  return cut.length === 0 ? null : cut;
}

/**
 * `slice` counts UTF-16 units, so cutting at a fixed length can land between the halves of a
 * surrogate pair and strand one — an emoji, or any astral character, becomes `\ufffd` once the text
 * round-trips through UTF-8. Backs the cut off by one unit when that happens.
 */
export function cutAtCodePoint(text: string, max: number): string {
  if (text.length <= max) return text;
  const lastUnit = text.charCodeAt(max - 1);
  const isHighSurrogate = lastUnit >= 0xd800 && lastUnit <= 0xdbff;
  return text.slice(0, isHighSurrogate ? max - 1 : max);
}

export type ChatSession = {
  id: string;
  userId: string;
  workspaceId: string | null;
  dashboardId: string | null;
  /** Bound routine id for a RoutineBuilder authoring session (Workflows v2, P12). Null otherwise. */
  workflowId: string | null;
  /** Bound audience id for a dedicated audience-agent session (generative audiences, ACT2-492). Null
   * otherwise. Optional so existing ChatSession literals (and sibling-lane test fixtures) stay valid. */
  audienceId?: string | null;
  /** Bound rETL connection id for an activation failure chat session. Null otherwise. Optional for
   * the same reason as `audienceId`. */
  activationConnectionId?: string | null;
  title: string | null;
  visibility: 'private' | 'workspace' | 'public';
  slug: string | null;
  sdkSessionId: string | null;
  /** JSON array of context_entry ids for skills slash-invoked into this session. */
  loadedSkillIds: string[];
  /** Demo/challenge-workspace budget stop (ACT2 self-serve sandbox): set once a turn in this thread hit
   * the SDK's `error_max_budget_usd` result subtype — see `markSessionBudgetExhausted` (src/db/sessions.ts).
   * Optional so existing ChatSession literals (and sibling-lane test fixtures) stay valid; a normal
   * workspace's session never sets it. */
  budgetExhausted?: boolean;
  createdAt: string;
  updatedAt: string;
};

// ─── Dashboard types ───

export type DashboardParam = {
  name: string;
  label: string;
  type: 'time_range' | 'select' | 'multi_select';
  required: boolean;
  default?: string | string[];
  options?: { label: string; value: string }[];
  sql?: string; // dynamic: standalone SELECT, no {{placeholders}}
  // time_range only. Rolling: /^\d+[dmy]$/ (e.g. '7d', '3m', '1y').
  // Calendar: 'today' | 'this_week' | 'this_month' | 'this_year'.
  // When set, dates are resolved server-side and the UI shows a read-only badge.
  fixed_preset?: string;
};

export type DashboardPanel = {
  id: string;
  title: string;
  type: 'chart' | 'table' | 'metric' | 'funnel';
  sql: string;
  chartType?: 'bar' | 'line' | 'area' | 'pie';
  metricConfig?: {
    format?: 'number' | 'percent' | 'currency';
    prefix?: string;
    suffix?: string;
  };
  funnelConfig?: {
    stepColumn: string;
    valueColumn: string;
  };
  width?: 'full' | 'half';
  order: number;
};

export type DashboardSpec = {
  parameters: DashboardParam[];
  panels: DashboardPanel[];
};

/** Live dashboard preview draft — ephemeral sidebar state (AI-715). */
/** Character budget for draft/send and persisted dashboard JSON (shared). */
export const DASHBOARD_SPEC_JSON_MAX = 100_000;
export const DASHBOARD_MAX_PANELS = 50;
export const DASHBOARD_MAX_PARAMETERS = 50;
export const DASHBOARD_ARTIFACTS_INVENTORY_MAX = 50;

export const DashboardDraftSnapshotSchema = z
  .object({
    title: z.string().max(500),
    slug: z.string().max(80),
    warehouseId: z.string().max(200),
    description: z.string().max(10_000).nullable().optional(),
    spec: z.object({
      parameters: z.array(z.record(z.string(), z.unknown())).max(DASHBOARD_MAX_PARAMETERS),
      panels: z.array(z.record(z.string(), z.unknown())).max(DASHBOARD_MAX_PANELS),
    }),
  })
  .superRefine((val, ctx) => {
    // Measure the spec only — same as validateSpec's cap — so a spec that saves (spec-only < limit)
    // can't be rejected here by the extra title/slug/warehouse/description wrapper bytes, which would
    // wedge chat.sendMessage for that draft. The wrapper fields are individually bounded above.
    if (JSON.stringify(val.spec).length > DASHBOARD_SPEC_JSON_MAX) {
      ctx.addIssue({
        code: 'custom',
        message: `Dashboard draft exceeds ${String(DASHBOARD_SPEC_JSON_MAX)} character limit`,
      });
    }
  });
export type DashboardDraftSnapshot = z.infer<typeof DashboardDraftSnapshotSchema>;

/** Character budget for the audience draft the page sends back with each chat message. Generous next to
 * a real definition (a large tree is a few kB) and small enough that the envelope stays cheap. */
export const AUDIENCE_DEFINITION_JSON_MAX = 60_000;

/**
 * The unsaved audience edit currently open on the audience page (ACT2-623).
 *
 * The page holds it in memory only — the agent proposes it, the SSE event delivers it, and the page
 * sends it back on the next message so the agent keeps its own proposal in view across turns. A page
 * reload drops it, which is the intended recovery model.
 *
 * `definition` is `unknown` here ON PURPOSE. `parseAudienceDefinition` is the one total entry point for
 * the contract (see the eslint ban in `services/audience.ts`), so this schema bounds the SIZE and the
 * RPC validates the SHAPE. Nothing else from the client is trusted: the audience's status, title,
 * warehouse and count all stay server-derived.
 */
export const AudienceDraftSnapshotSchema = z
  .object({
    definition: z.unknown(),
    /**
     * The page's monotonic draft revision. Optional: a missing or unparseable value degrades to
     * "match by hash only". Invalid values are dropped rather than failing the whole draft — a
     * bad revision must not take a well-formed definition with it.
     */
    revision: z.number().int().nonnegative().optional().catch(undefined),
  })
  .superRefine((val, ctx) => {
    // `z.unknown()` admits values `JSON.stringify` throws on (BigInt, circular). `.catch()` on the
    // outer schema converts ZodErrors only, not a throw from this body, so an uncaught TypeError
    // would 500 the procedure instead of degrading to "no proposal".
    let serialized: string;
    try {
      // The lib types say stringify returns `string`. It RETURNS undefined for a function or a symbol,
      // and `.length` on that throws the same TypeError this block exists to stop.
      const raw = JSON.stringify(val.definition ?? null);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see above: the lib type is wrong
      serialized = raw ?? 'null';
    } catch {
      ctx.addIssue({
        code: 'custom',
        message: 'Audience draft is not JSON-serializable',
      });
      return;
    }
    if (serialized.length > AUDIENCE_DEFINITION_JSON_MAX) {
      ctx.addIssue({
        code: 'custom',
        message: `Audience draft exceeds ${String(AUDIENCE_DEFINITION_JSON_MAX)} character limit`,
      });
    }
  });
export type AudienceDraftSnapshot = z.infer<typeof AudienceDraftSnapshotSchema>;

export const DashboardArtifactInventoryItemSchema = z.object({
  slug: z.string().max(80),
  title: z.string().max(500),
  saved: z.boolean(),
  /** Present when `saved` — required for `update_dashboard`. */
  dashboardId: z.string().max(200).optional(),
});
export type DashboardArtifactInventoryItem = z.infer<typeof DashboardArtifactInventoryItemSchema>;

/**
 * Truncate (do not reject) when a chat has more artifacts than the agent preamble budget, and drop
 * individual malformed items rather than losing the whole inventory — this feeds the agent preamble via
 * `.optional().catch(undefined)`, so one bad entry must not strip every dashboard id from the agent.
 * Only the first `MAX` items are validated (bounds work regardless of input array length).
 */
export const DashboardArtifactsInventorySchema = z.array(z.unknown()).transform((items) => {
  const parsed: DashboardArtifactInventoryItem[] = [];
  for (const item of items.slice(0, DASHBOARD_ARTIFACTS_INVENTORY_MAX)) {
    const result = DashboardArtifactInventoryItemSchema.safeParse(item);
    if (result.success) parsed.push(result.data);
  }
  return parsed;
});

export type Dashboard = {
  id: string;
  workspaceId: string;
  createdBy: string | null;
  slug: string;
  title: string;
  description: string | null;
  warehouseId: string;
  isPublic: boolean;
  shareTokenHash: string | null;
  spec: DashboardSpec;
  createdAt: string;
  updatedAt: string;
  /**
   * ACT2-1286: which database the dashboard needs (`TYPE|host|database`), and a hint of the account
   * that last worked for it — a plain id that may name a deleted account. Written by every save (an
   * unmoved save keeps it); with account repair on, readers resolve through these, otherwise through
   * `warehouseId`. Never on {@link PublicDashboard}.
   */
  accountKey: string | null;
  lastAccountId: string | null;
};

export type PublicDashboardPanel = {
  id: string;
  title: string;
  type: DashboardPanel['type'];
  chartType?: DashboardPanel['chartType'];
  metricConfig?: DashboardPanel['metricConfig'];
  funnelConfig?: DashboardPanel['funnelConfig'];
  width?: DashboardPanel['width'];
  order: number;
};

export type PublicDashboardParam = {
  name: string;
  label: string;
  type: DashboardParam['type'];
  required: boolean;
  default?: DashboardParam['default'];
  options?: DashboardParam['options'];
  hasDynamicOptions: boolean;
  fixed_preset?: string;
};

export type PublicDashboard = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  spec: { parameters: PublicDashboardParam[]; panels: PublicDashboardPanel[] };
};

// ─── Workspace types ───

export const BUILT_IN_SKILL_ID_PREFIX = 'builtin:' as const;

export type WorkspaceRole = 'owner' | 'admin' | 'member';

export type Workspace = {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
};

export type WorkspaceMember = {
  workspaceId: string;
  userId: string;
  role: WorkspaceRole;
  joinedAt: string;
  user: User;
  /** ACT2-1124: when the membership check of a sibling workspace first saw no RudderStack access; null otherwise. */
  rudderstackMissingSince?: string | null;
};

// `invalid` = a legacy invite row with no email; `pending` is the only acceptable state.
export type InviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired' | 'invalid';

export type InviteLink = {
  id: string;
  workspaceId: string;
  createdBy: string;
  email: string;
  role: Exclude<WorkspaceRole, 'owner'>;
  status: InviteStatus;
  expiresAt: string;
  lastSentAt: string;
  acceptedAt: string | null;
  acceptedBy: string | null;
  isRevoked: boolean;
  createdAt: string;
  /** ACT2-1124: set on a RudderStack invitation, null on a Lookout invite. */
  rudderstackInvitationId?: string | null;
  /** ACT2-1124: when the RudderStack accept page validated this invitation's token. */
  acceptStartedAt?: string | null;
};

/**
 * Whether the signed-in caller can accept this invite, and why not when they cannot.
 * `ok` also covers a signed-out visitor and an existing member — neither is blocked on the address.
 * `wrong_account` means they do not hold the invited address. `unproven` means they hold it on a
 * login nothing has proven yet, which is a different screen and a different remedy (ACT2-791).
 */
export type InviteEmailStatus = 'ok' | 'wrong_account' | 'unproven';

export type ActiveInviteInfo = {
  status: 'pending';
  workspaceName: string;
  maskedEmail: string | null;
  alreadyMember: boolean;
  emailStatus: InviteEmailStatus;
};

export type AuthorizedActiveInviteInfo = ActiveInviteInfo & {
  inviterDisplayName: string;
  inviterAvatarUrl: string | null;
};

export type TerminalInviteInfo = {
  status: Exclude<InviteStatus, 'pending'>;
  alreadyMember: boolean;
};

/** Public invite-page payload. Inviter identity is present only for an authorized caller. */
export type InviteInfoResponse = ActiveInviteInfo | AuthorizedActiveInviteInfo | TerminalInviteInfo;

export type WorkspaceSettings = {
  workspaceId: string;
  rudderstackWorkspaceId: RudderstackWorkspaceId | null;
  rudderstackSourceId: string | null;
  rudderstackPat: string | null;
  useTeamNotesInAi: boolean;
  useGeneratedContextInAi: boolean;
  allowChatMemoryCapture: boolean;
  requireMemoryApproval: boolean;
  publicSharingEnabled: boolean;
  slackGuestAccessEnabled: boolean;
  /** True when linked with a read-only (RudderStack `member`-role) PAT — restricts the agent to read-only tools. */
  rudderstackReadOnly: boolean;
  /**
   * ACT2-1124: the RudderStack organization of a sibling workspace. A value marks the sibling model, where
   * `rudderstackWorkspaceId` is the sibling and no shared key exists.
   */
  rudderstackOrganizationId?: string | null;
  /** ACT2-1124: the stored region of a sibling workspace (`US` or `EU`). */
  rudderstackRegion?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkspaceSettingsRedacted = {
  workspaceId: string;
  rudderstackWorkspaceId: RudderstackWorkspaceId | null;
  /** Stored link presence, available even when the workspace id is redacted; not a verification verdict. */
  rudderstackLinked: boolean;
  rudderstackSourceId: string | null;
  rudderstackPat: string | null;
  useTeamNotesInAi: boolean;
  useGeneratedContextInAi: boolean;
  allowChatMemoryCapture: boolean;
  requireMemoryApproval: boolean;
  publicSharingEnabled: boolean;
  slackGuestAccessEnabled: boolean;
  rudderstackReadOnly: boolean;
  /** ACT2-1124: the RudderStack organization of a sibling workspace, null for a shared-key one. */
  rudderstackOrganizationId?: string | null;
};

// ─── Context Entry types ───

/** `mcp`: written by an owner/admin over the external MCP server (ACT2-1129). */
export type ContextEntrySourceType = 'manual' | 'chat' | 'cleanup' | 'mcp';
export type NoteCategory = 'tables' | 'metrics' | 'terminology' | 'general';

export type ContextEntry = {
  id: string;
  workspaceId: string;
  category: string;
  entryType: 'note' | 'memory' | 'skill';
  sourceType: ContextEntrySourceType;
  sourceRef: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdViaAi: boolean;
  title: string;
  content: string;
  skillDescription: string | null;
  skillEnabled: boolean;
  reviewStatus: 'proposed' | 'approved' | 'superseded';
  reviewedBy: string | null;
  reviewedAt: string | null;
  supersededBy: string | null;
  analysisStatus: 'pending' | 'running' | 'completed' | 'failed';
  analysisError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Skill = {
  id: string;
  workspaceId: string;
  name: string;
  description: string;
  instructions: string;
  enabled: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

/** List/menu projection — omits the instruction body (fetched via `getSkill` / `skills.get`). */
export type SkillSummary = Omit<Skill, 'instructions'>;

/** Slash-menu projection spanning workspace-backed and code-defined skills. */
export type InvokableSkillSummary = Pick<SkillSummary, 'id' | 'name' | 'description' | 'enabled'> & {
  source: 'workspace' | 'built_in';
};

/** Shared skill write/list caps — keep service, RPC Zod, UI `maxLength`, and migration in lockstep. */
export const MAX_SKILLS_PER_WORKSPACE = 20;
export const MAX_SKILLS_PER_CHAT = 5;
export const MAX_SKILL_NAME = 200;
export const MAX_SKILL_DESCRIPTION = 400;
export const MAX_SKILL_INSTRUCTIONS = 100_000;
/**
 * Built-in skills are TypeScript constants compiled into the bundle, so none of the workspace-skill
 * write path applies to them: not the skill service, not the RPC input schema, not the editor's
 * maxLength. They get their own, larger bound. It is a runaway guard, not a product limit — what
 * actually constrains a built-in is the turn, since the body is injected whole and every character
 * competes with the evidence the run still has to gather.
 *
 * Keep it above `MAX_SKILL_INSTRUCTIONS`: a built-in is authored and reviewed in the repository, where
 * an oversized body is caught by a test, rather than pasted by a user into a form.
 */
export const MAX_BUILT_IN_SKILL_INSTRUCTIONS = 200_000;

// ─── Chat panel references ───

/**
 * A dashboard panel referenced from the chat composer.
 *
 * Re-exported from `lib/panel-mentions.ts`, which owns the rule that builds the label. The id is
 * authoritative — titles are not unique, which is why the label exists at all.
 */
export type { PanelMention } from './lib/panel-mentions.js';

// ─── Doc types ───

export type Doc = {
  id: string;
  workspaceId: string;
  docType: 'source' | 'warehouse';
  sourceId: string | null;
  warehouseId: string | null;
  accountKey: string | null;
  name: string;
  typeLabel: string | null;
  repoFullName: string | null;
  status: 'generating' | 'ready' | 'failed';
  spec: WarehouseDocSpec | null;
  commitSha: string | null;
  pendingCommitSha: string | null;
  error: string | null;
  generationStartedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ContextHubItemKind = 'generated' | 'note' | 'memory' | 'skill';
/** `github_repo` was dropped with the source-docs vertical (ACT2-414) — nothing produces it. */
export type ContextHubItemSourceType = 'warehouse' | ContextEntrySourceType;
export type ContextHubItemStatus = 'generating' | 'ready' | 'failed';

/** Scan-applied kinds that produce cleanup provenance (excludes capture `new`). */
export const CLEANUP_SUGGESTION_TYPES = ['duplicate', 'merge', 'conflict'] as const;
export type MemoryCleanupSuggestionType = (typeof CLEANUP_SUGGESTION_TYPES)[number];

export type MemoryCleanupProvenance = {
  suggestionType: MemoryCleanupSuggestionType;
  sourceCount: number;
  resolvedAt: string;
  reviewerName: string | null;
};

export type ContextHubItem = {
  id: string;
  kind: ContextHubItemKind;
  title: string;
  summary: string;
  content: string | null;
  searchText: string | null;
  status: ContextHubItemStatus;
  sourceType: ContextHubItemSourceType;
  sourceRef: string | null;
  sourceLabel: string | null;
  editable: boolean;
  createdViaAi: boolean;
  cleanupProvenance: MemoryCleanupProvenance | null;
  category: string | null;
  docType: 'source' | 'warehouse' | null;
  typeLabel: string | null;
  repoFullName: string | null;
  skillDescription: string | null;
  skillEnabled: boolean;
  /** Creator user id for notes/memories; null for generated docs (workspace-owned infra). */
  createdBy: string | null;
  reviewStatus: 'proposed' | 'approved' | 'superseded' | null;
  analysisStatus: 'pending' | 'running' | 'completed' | 'failed' | null;
  analysisError: string | null;
  createdAt: string;
  updatedAt: string;
  /** Generated docs only: the account key the doc describes (ACT2-1133). */
  accountKey?: string | null;
  /**
   * Generated docs only (ACT2-1133): whether an account with the doc's `accountKey` exists in the
   * workspace. A Disconnected doc stays until it is deleted; Context Hub says why it is stale.
   */
  connected?: boolean;
};

export type ContextReviewSuggestionType = 'new' | 'duplicate' | 'merge' | 'conflict';

export type ContextReviewSuggestion = {
  id: string;
  origin: 'capture' | 'scan' | 'chat';
  suggestionType: ContextReviewSuggestionType;
  proposalEntryId: string | null;
  sourceMemoryIds: string[];
  relatedContextRefs: Array<{ kind: 'generated' | 'note' | 'memory' | 'skill'; id: string; title: string }>;
  proposedTitle: string | null;
  proposedContent: string | null;
  rationale: string;
  status: 'pending' | 'applied';
  createdAt: string;
};

export type ContextReviewRun = {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  error: string | null;
  suggestionCount: number;
  memoriesScanned: number;
  memoriesTotal: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};
// ─── Workflows v2 (graph workflows) ───
// Authoritative wire/storage shapes. The graph is pure structure (nodes + edges); `kind` lives on
// the Workflow, outside the graph. v0 is Slack-only (no email sink). The condition predicate is a
// typed operator enum — never an evaluated string — so the executor has zero eval/injection surface.

export const WorkflowKindSchema = z.enum(['notification', 'alert']);
export type WorkflowKind = z.infer<typeof WorkflowKindSchema>;

/** Returns true when `tz` is a valid IANA timezone identifier. */
function isValidIanaTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const WorkflowTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('manual') }),
  z
    .object({
      type: z.literal('recurring'),
      // Validated by parseCron: must be a 5-field cron from the supported cadence set
      // (hourly/daily/weekdays/weekly/monthly) exactly as emitted by cadenceToCron.
      cron: z.string().min(1),
      // Must be a valid IANA timezone identifier accepted by the Intl API.
      timezone: z.string().min(1),
    })
    .refine(
      (val) => {
        try {
          parseCron(val.cron);
          return true;
        } catch {
          return false;
        }
      },
      { message: 'cron must be a supported 5-field cadence (hourly/daily/weekdays/weekly/monthly)', path: ['cron'] },
    )
    .refine((val) => isValidIanaTimezone(val.timezone), {
      message: 'timezone must be a valid IANA timezone identifier (e.g. "America/New_York", "UTC")',
      path: ['timezone'],
    }),
]);
export type WorkflowTrigger = z.infer<typeof WorkflowTriggerSchema>;

/**
 * A routine description: a concise, single-line summary (max 280 chars) shown in the routine
 * list/detail. `trim()` normalizes surrounding whitespace so whitespace-only input fails `min(1)`;
 * the refine rejects line breaks so it stays the one-liner every "concise one-line summary" prompt
 * promises. Shared by the `save_workflow` and `routine_update_meta` MCP tools so their validation
 * can't drift apart (add `.optional()` at the callsite where the field is optional).
 */
export const WorkflowDescriptionSchema = z
  .string()
  .trim()
  .min(1)
  .max(280)
  .refine((value) => !/[\r\n]/.test(value), { message: 'Description must be a single line (no line breaks).' });

// `query` config is a discriminated union on `mode` so adding a future `savedQueryId` mode can't
// produce invalid states (e.g. both sql and savedQueryId). v0 ships only the `sql` mode.
// `connectionId` (phase 1.5, AI-1370) is LEGACY: no writer mints it any more. It stays declared so a
// `z.object` does not strip it before the ACT2-1286 backfill reads it as a node's hint.
// `accountKey` / `lastAccountId` / `lastIdentityHash` (ACT2-1286) are the account soft-link, the same
// triple the item tables carry. They are DERIVED, never authored: the workflow service stamps them on
// every save (`stampQueryNodeAccountLinks`), and ignores whatever a client sent.
// It MUST stay in lockstep with the duplicate copy in `lib/workflow/nodes/query.ts` — a `z.object`
// strips unknown keys, so a field declared in only one of the two is deleted on the next autosave.
export const QueryNodeConfigSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('sql'),
    sql: z.string().min(1),
    warehouseId: z.string().min(1),
    connectionId: z.uuid().nullable().optional(),
    accountKey: z.string().min(1).nullable().optional(),
    lastAccountId: z.uuid().nullable().optional(),
    lastIdentityHash: z.string().nullable().optional(),
    // ACT2-1286 PR 2: the relink record a confirmed repair writes on this node. Derived, never authored.
    accountRelinkedAt: z.string().nullable().optional(),
    accountRelinkSnapshot: AccountRelinkSnapshotSchema.nullable().optional(),
    // The `warehouseId` that repair replaced, so a save from an editor opened before the repair (it
    // still sends this id) is refused instead of undoing the repair. Derived, never authored.
    accountRelinkedFromWarehouseId: z.string().nullable().optional(),
  }),
]);
export type QueryNodeConfig = z.infer<typeof QueryNodeConfigSchema>;

/**
 * Strict threshold rule for row_count — a whole, non-negative count (also rejects NaN/Infinity via
 * `.int()`). This is enforced at the USER-INITIATED action boundaries (save / dry-run / runNow, via
 * `validateConditionThresholds`) and at the agent's `routine_draft` tool input. It is deliberately NOT
 * part of the persisted/transport {@link ConditionConfigSchema} (see there): legacy graphs saved before
 * this tightened may carry fractional/negative values, and those still need to load, autosave, and
 * evaluate on their schedule. The clean messages are surfaced to the user when a boundary rejects.
 */
export const ConditionRhsSchema = z
  .number()
  .int('Condition threshold must be a whole number (no decimals).')
  .nonnegative('Condition threshold must be zero or greater.');

export const ConditionConfigSchema = z.object({
  lhs: z.enum(['row_count']),
  op: z.enum(['>', '>=', '<', '<=', '==', '!=']),
  // Persisted/transport/execute schema is LENIENT — `z.number().optional()`:
  //  - lenient value (any number): legacy graphs saved before the whole-≥0 rule still load, autosave,
  //    dry-run, and run on schedule; the scheduled executor soft-accepts + warns.
  //  - optional: an autosaved WORKING COPY of a draft-in-progress may have an emptied threshold, which
  //    is stored as an absent `rhs` (not coerced to 0) so a cleared value round-trips back to an empty
  //    field on reload instead of resurrecting `row_count <op> 0`.
  // The strict whole-≥0 rule ({@link ConditionRhsSchema}) — which also rejects an ABSENT rhs — is
  // enforced at the save / dry-run / runNow boundaries (`validateConditionThresholds`), so a published,
  // executable routine always carries a valid threshold. The executor guards NaN/Infinity (and a missing
  // rhs on a malformed persisted graph) as the last line of defense.
  rhs: z.number().optional(),
});

export const WorkflowNodeSchema = z.discriminatedUnion('kind', [
  z.object({ id: z.string().min(1), kind: z.literal('query'), config: QueryNodeConfigSchema }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('dashboard'),
    config: z.object({ dashboardId: z.string().min(1) }),
  }),
  z.object({ id: z.string().min(1), kind: z.literal('condition'), config: ConditionConfigSchema }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('slack'),
    config: z.object({ channel: z.string().min(1), template: z.string().min(1) }),
  }),
]);
export type WorkflowNode = z.infer<typeof WorkflowNodeSchema>;

// `when` is only meaningful on edges leaving a `condition` node.
export const WorkflowEdgeSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  when: z.enum(['true', 'false']).optional(),
});
export type WorkflowEdge = z.infer<typeof WorkflowEdgeSchema>;

export const WorkflowGraphSchema = z.object({
  nodes: z.array(WorkflowNodeSchema).min(1),
  edges: z.array(WorkflowEdgeSchema),
});
export type WorkflowGraph = z.infer<typeof WorkflowGraphSchema>;

export const WorkflowStatusSchema = z.enum(['draft', 'active', 'paused']);
export type WorkflowStatus = z.infer<typeof WorkflowStatusSchema>;

export const WorkflowSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  description: z.string(),
  kind: WorkflowKindSchema,
  trigger: WorkflowTriggerSchema,
  graph: WorkflowGraphSchema,
  status: WorkflowStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * ACT2-1286 PR 2: set when the workflow was paused because a query node's warehouse account was
   * removed. While set (and the repair flag is on), Run now and agent activation are refused and
   * only an admin Resume after a passing probe clears it. Optional so hand-built fixtures still type.
   */
  accountRepairPausedAt: z.string().nullable().optional(),
});
export type Workflow = z.infer<typeof WorkflowSchema>;

// ─── RoutineBuilder authoring wire contract (P12 — AI-928) ───
// A *routine* is the user/agent-facing subset of a Workflow (scheduled query → optional logic →
// destination). RoutineBuilder authors it interactively; its draft compiles down to a WorkflowGraph
// run by the unchanged workflow engine. Per the naming rule, the engine substrate stays `Workflow*`;
// these new agent/editor-facing types take the `Routine` prefix.
//
// Both schemas carry the PERSISTED vocabulary (the same `WorkflowTrigger` / `ConditionConfig` shapes
// the graph stores), so an agent patch and a server-loaded workflow land in byte-identical editor
// state after the one shared mapper (the editor reducer flattens persisted → view-model). This is the
// single contract the backend draft tools and the frontend editor both depend on.

// The live draft-patch event (P12 §3.1–3.2): a dedicated ephemeral SSE side-channel (peer of
// `title_change`, NOT a durable chat bubble). A discriminated union on `stage`; each variant carries
// exactly that stage's data so it maps 1:1 to the editor's per-step fields. `workflow_update_meta`
// emits up to two patches (one `name`, one `schedule`) — hence the clean one-field-family-per-stage
// split. A patch compiles toward a WorkflowGraph.
export const RoutineDraftPatchSchema = z.discriminatedUnion('stage', [
  z.object({ stage: z.literal('name'), name: z.string() }),
  z.object({ stage: z.literal('description'), description: z.string() }),
  z.object({ stage: z.literal('schedule'), trigger: WorkflowTriggerSchema }),
  // query/destination fields are individually optional so a patch can fill one field at a time as the
  // agent narrows the build; the editor merges into the controlled step inputs.
  z.object({ stage: z.literal('query'), warehouseId: z.string().optional(), sql: z.string().optional() }),
  // `condition: null` is the explicit "clear the condition" signal (the agent's `{type:'none'}` logic).
  z.object({ stage: z.literal('logic'), condition: ConditionConfigSchema.nullable() }),
  // The destination stage carries the deterministic slack template fields. Fields are individually
  // optional so a partial patch can fill one field at a time.
  z.object({
    stage: z.literal('destination'),
    channel: z.string().optional(),
    template: z.string().optional(),
  }),
]);
export type RoutineDraftPatch = z.infer<typeof RoutineDraftPatchSchema>;

// A single soft-validation issue surfaced on a snapshot (P12 §2.4). `code` is a structured machine
// code (e.g. `incomplete_draft`, `unknown_token`); `field` optionally points at the offending stage
// field. Issues on a *successful* snapshot mean "draft updated but not yet save-valid".
export const RoutineDraftIssueSchema = z.object({
  code: z.string(),
  message: z.string(),
  field: z.string().optional(),
});
export type RoutineDraftIssue = z.infer<typeof RoutineDraftIssueSchema>;

// The per-turn snapshot envelope (P12 §2.4): what the modify/read tools return AND what the UI sends
// to the agent at turn start. `draft` mirrors the server-side `AgentWorkflowDraft` in persisted
// vocabulary; `kind`/`trigger` are auto-managed (condition present ⇒ alert) and always present. Only
// `query` and `destination` are tracked in `missingStages` (logic is optional). `graph` is present
// only when the draft is complete (zero issues), produced by `assembleGraph`.
export const RoutineDraftSnapshotSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  kind: WorkflowKindSchema,
  trigger: WorkflowTriggerSchema,
  query: z.object({ sql: z.string(), warehouseId: z.string() }).optional(),
  // persisted-vocabulary condition; absent = no condition node.
  logic: ConditionConfigSchema.optional(),
  destination: z.object({ kind: z.literal('slack'), channel: z.string(), template: z.string() }).optional(),
  queryColumns: z.array(z.string()).optional(),
});
export type RoutineDraftSnapshot = z.infer<typeof RoutineDraftSnapshotSchema>;

export const RoutineDraftWireSchema = z.object({
  draft: RoutineDraftSnapshotSchema,
  complete: z.boolean(),
  issues: z.array(RoutineDraftIssueSchema),
  missingStages: z.array(z.enum(['query', 'destination'])),
  graph: WorkflowGraphSchema.optional(),
});
export type RoutineDraftWire = z.infer<typeof RoutineDraftWireSchema>;

// ─── Interactive chat blocks (AI-411) ───
// Agent-emitted question/approval blocks that pause the turn mid-flight (via canUseTool) to collect
// structured input, then resume the SAME turn with the answer as the tool result. See
// docs/superpowers/plans/2026-07-27-chat-interactive-blocks.md. The agent supplies the payload
// WITHOUT blockId (canUseTool assigns it). Markdown renders in the approval description and each
// option's description (inline); question prompts and option labels are plain text. Important
// numbers go in `stat`/`stats`, never buried in prose.

/** A per-option number, rendered right-aligned + tabular-nums (value + optional unit). */
export const InteractiveStatSchema = z.object({
  value: z.string().min(1),
  unit: z.string().optional(),
});
export type InteractiveStat = z.infer<typeof InteractiveStatSchema>;

/** A card-header stat (value + optional label) — distinct from the per-option InteractiveStat. */
export const InteractiveHeaderStatSchema = z.object({
  value: z.string().min(1),
  label: z.string().optional(),
});
export type InteractiveHeaderStat = z.infer<typeof InteractiveHeaderStatSchema>;

/**
 * The radio value the host's free-text "Other" row carries. Single-select rows are keyed by option
 * LABEL, so an authored option with this exact label would collide with that row and be read back as
 * manual entry. Exported so the UI uses this string rather than its own copy, and stripped in
 * `normalizeQuestionInput` so no payload can carry it in the first place — a reserved name, not a
 * string the leading space merely makes unlikely.
 */
export const MANUAL_ENTRY_ROW_VALUE = ' __manual_entry__';

export const QuestionOptionSchema = z.object({
  label: z.string().min(1).max(200).describe('Plain-text label shown to the user.'),
  description: z.string().max(2000).optional().describe('Optional inline markdown under the label.'),
  stat: InteractiveStatSchema.optional().describe(
    'Optional right-aligned number (value + optional unit). Put counts here, never in the label.',
  ),
  // Stable machine identity returned in the answer (`values`) and the one the agent should branch on —
  // distinct from the display `label`, so rewording a label never changes the recorded answer.
  value: z
    .string()
    .min(1)
    .max(200)
    .describe('Stable machine identity returned in the answer `values`; distinct from the display label.'),
});
export type QuestionOption = z.infer<typeof QuestionOptionSchema>;

const QuestionObjectSchema = z
  .object({
    id: z.string().min(1).describe('Stable id for this question; answer entries key on it.'),
    prompt: z.string().min(1).max(500).describe('Plain-text question shown as the card title.'),
    multiSelect: z
      .boolean()
      .describe(
        'true when the answer is a set (which plan names count as paid, which columns to include); false when alternatives are mutually exclusive (which table, which time window).',
      ),
    options: z
      .array(QuestionOptionSchema)
      // The message names the repair, because the commonest way to land here is not authoring one
      // option — it is authoring two where one is labelled "Other", which `normalizeQuestionInput`
      // strips before this runs. Without saying so, the model's retry has nothing to converge on.
      .min(2, {
        message:
          'Needs at least 2 options. Note: an option labelled "Other" is reserved for the built-in free-text row and is removed before validation — if you meant it as a real value, give it a concrete label (e.g. "Other channel").',
      })
      .max(20)
      .describe(
        'At least two options. Do not add a catch-all "Other" / "Something else" option — the UI always offers a free-text Other row.',
      ),
    /** multiSelect-only soft limits — Submit enables at >= min; at max, extra rows dim + amber strip.
     * Rejected on single-select (see superRefine), where the UI has no min/max affordance. */
    min: z
      .number()
      .int()
      .positive()
      .optional()
      .describe('Multi-select only: Submit enables at >= min. Do not set on a single-select.'),
    max: z
      .number()
      .int()
      .positive()
      .optional()
      .describe('Multi-select only: at max, extra rows dim. Do not set on a single-select.'),
    /** Option labels pre-selected on first render. Must match option labels exactly. */
    defaults: z
      .array(z.string())
      .optional()
      .describe('Option labels pre-selected on first render. Must match option labels exactly.'),
  })
  .superRefine((q, ctx) => {
    // `label` is the option's identity (React key, control value, answer text), so duplicates would
    // collapse two options into one; reject them at the source.
    const labels = q.options.map((o) => o.label);
    if (new Set(labels).size !== labels.length) {
      ctx.addIssue({ code: 'custom', message: 'Question options must have unique labels', path: ['options'] });
    }
    // The answer identifies each choice by its `value`, so values must be unique too (not just labels).
    const values = q.options.map((o) => o.value);
    if (new Set(values).size !== values.length) {
      ctx.addIssue({ code: 'custom', message: 'Question option `value`s must be unique', path: ['options'] });
    }
    // A default that doesn't match an option label would seed an invisible, unremovable selection.
    const labelSet = new Set(labels);
    for (const [i, d] of (q.defaults ?? []).entries()) {
      if (!labelSet.has(d)) {
        ctx.addIssue({
          code: 'custom',
          message: `defaults[${i}] "${d}" is not one of the option labels`,
          path: ['defaults', i],
        });
      }
    }
    // Contradictory limits leave Submit permanently un-enableable.
    if (q.min != null && q.max != null && q.min > q.max) {
      ctx.addIssue({ code: 'custom', message: 'min must be <= max', path: ['min'] });
    }
    // min/max are multiSelect-only knobs; on a single-select the UI ignores them, so a bound set here
    // would silently do nothing. `normalizeQuestionInput` already promotes or drops every such case,
    // so this is an unreachable backstop kept in case the repair ever narrows.
    if (!q.multiSelect) {
      if (q.min != null) {
        ctx.addIssue({ code: 'custom', message: 'min applies only to multiSelect questions', path: ['min'] });
      }
      if (q.max != null) {
        ctx.addIssue({ code: 'custom', message: 'max applies only to multiSelect questions', path: ['max'] });
      }
    }
    // multiSelect-only satisfiability: a min above the number of selectable rows (options + always-on
    // "Other") can never be reached, and more defaults than max seeds an over-limit selection the user
    // is forced to trim before submitting. Both leave Submit stuck, same class as min>max above.
    if (q.multiSelect) {
      if (q.min != null) {
        const selectable = q.options.length + 1;
        if (q.min > selectable) {
          ctx.addIssue({
            code: 'custom',
            message: `min (${q.min}) exceeds the number of selectable options (${selectable})`,
            path: ['min'],
          });
        }
      }
      if (q.max != null && (q.defaults?.length ?? 0) > q.max) {
        ctx.addIssue({
          code: 'custom',
          message: `defaults has more entries (${q.defaults?.length}) than max (${q.max})`,
          path: ['defaults'],
        });
      }
    }
  });

/**
 * Model-authored payloads are repaired (see `normalizeQuestionInput`) as part of the parse, so every
 * consumer — the interactive gate AND the `ask_question` tool's own arg validation in `src/mcp/ui.ts`
 * — gets the same repaired question. Keeping the repair outside the schema let the two disagree.
 */
export const QuestionSchema = z.preprocess(normalizeQuestionInput, QuestionObjectSchema);
export type Question = z.infer<typeof QuestionSchema>;

/** Full question-block payload (post-assignment) — persisted + sent to the client. */
const questionBlockShape = {
  blockId: z.string().min(1),
  // 1 question = single/multi-select; 2..5 = wizard.
  questions: z.array(QuestionSchema).min(1).max(5),
  stats: z.array(InteractiveHeaderStatSchema).optional(),
};
// The model authors question ids and answer entries key on them, so a collision makes an entry
// ambiguous — reject duplicates across the block (per-question invariants live in QuestionSchema).
const rejectDuplicateQuestionIds = (block: { questions: { id: string }[] }, ctx: z.RefinementCtx): void => {
  const ids = block.questions.map((q) => q.id);
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: 'custom', message: 'question ids must be unique within a block', path: ['questions'] });
  }
};
export const QuestionBlockPayloadSchema = z.object(questionBlockShape).superRefine(rejectDuplicateQuestionIds);
export type QuestionBlockPayload = z.infer<typeof QuestionBlockPayloadSchema>;

/** Full approval payload (post-assignment). */
export const ApprovalPayloadSchema = z.object({
  blockId: z.string().min(1),
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(4000), // block markdown
  stats: z.array(InteractiveHeaderStatSchema).optional(),
  // Custom button labels are capped at 16 chars so the three action buttons stay compact + on one row.
  approveLabel: z.string().min(1).max(16).optional(),
  rejectLabel: z.string().min(1).max(16).optional(),
  reviseLabel: z.string().min(1).max(16).optional(),
  allowRevise: z.boolean().optional(),
});
export type ApprovalPayload = z.infer<typeof ApprovalPayloadSchema>;

/** What the agent provides to the ask tools (blockId is assigned server-side by canUseTool). */
export const QuestionBlockInputSchema = z
  .object(questionBlockShape)
  .omit({ blockId: true })
  .superRefine(rejectDuplicateQuestionIds);
export type QuestionBlockInput = z.infer<typeof QuestionBlockInputSchema>;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * Repair a model-authored question before the strict `QuestionObjectSchema` parse. Recoverable
 * mistakes (stray min/max on a single-select, a self-authored "Other" option, unmatched defaults, a
 * leftover `allowManualEntry` flag) are rewritten here; genuine ambiguity still fails the schema.
 */
function normalizeQuestionInput(q: unknown): unknown {
  const rec = asRecord(q);
  if (!rec) return q;
  const next: Record<string, unknown> = { ...rec };
  delete next['allowManualEntry'];

  const isMulti = next['multiSelect'] === true;
  if (!isMulti) {
    // max > 1 on a single-select means the model authored a set — promote. max === 1 (or a stray
    // min with no max>1) is equivalent to single-select, so drop the bounds.
    const max = next['max'];
    if (typeof max === 'number' && max > 1) {
      next['multiSelect'] = true;
      // The model declared pick-one, so a surviving floor would force MORE answers than the question
      // asks for — an exclusive question promoted with `min: 2` renders a card no one can answer.
      delete next['min'];
    } else {
      delete next['min'];
      delete next['max'];
    }
  }

  // The host always appends its own "Other" row, so a model-authored one renders twice under the same
  // name with a different answer shape. Drop it; `options.min(2)` still rejects a degenerate list.
  // The sentinel is dropped for a sharper reason: rows are keyed by label, so an option carrying it
  // would be indistinguishable from the free-text row when the answer is read back.
  const authored = next['options'];
  if (Array.isArray(authored)) {
    const kept = authored.filter((opt) => {
      const label = asRecord(opt)?.['label'];
      if (typeof label !== 'string') return true;
      return label.trim().toLowerCase() !== 'other' && label !== MANUAL_ENTRY_ROW_VALUE;
    });
    if (kept.length !== authored.length) next['options'] = kept;
  }

  const defaults = next['defaults'];
  const options = next['options'];
  if (Array.isArray(defaults) && Array.isArray(options)) {
    const labels = new Set(
      options.flatMap((opt) => {
        const optRec = asRecord(opt);
        if (!optRec) return [];
        const label = optRec['label'];
        return typeof label === 'string' ? [label] : [];
      }),
    );
    const kept = defaults.filter((d) => typeof d === 'string' && labels.has(d));
    if (kept.length === 0) delete next['defaults'];
    else next['defaults'] = kept;
  }

  return next;
}

export const ApprovalInputSchema = ApprovalPayloadSchema.omit({ blockId: true });
export type ApprovalInput = z.infer<typeof ApprovalInputSchema>;

/** Discriminated union describing the union of the two block payloads (for the awaiting_input event). */
export const InteractiveBlockPayloadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('question'), payload: QuestionBlockPayloadSchema }),
  z.object({ kind: z.literal('approval'), payload: ApprovalPayloadSchema }),
]);
export type InteractiveBlockPayload = z.infer<typeof InteractiveBlockPayloadSchema>;

// Free-text answers (`manual` text, and a multi-select's `otherText`) are bounded — they persist to
// sse_events and are echoed back into the model context as the tool result.
export const QuestionAnswerEntrySchema = z.discriminatedUnion('kind', [
  z.object({
    questionId: z.string().min(1),
    kind: z.literal('selected'),
    // Selected OPTION labels only — never the "Other" free text.
    labels: z.array(z.string().max(10_000)).max(50),
    // Machine identities parallel to `labels` (each option's `value`). Always present — the display
    // stays `labels`, the agent branches on `values`.
    values: z.array(z.string().max(10_000)).max(50),
    // Multi-select "Other" free text, kept DISTINCT from labels/values so it is never coerced through the
    // option map. Present when the user typed in the always-on Other row; a value equal to an option
    // label is therefore unambiguous free text, not a selection.
    otherText: z.string().max(10_000).optional(),
  }),
  z.object({ questionId: z.string().min(1), kind: z.literal('manual'), text: z.string().max(10_000) }),
  z.object({ questionId: z.string().min(1), kind: z.literal('skipped') }),
]);
export type QuestionAnswerEntry = z.infer<typeof QuestionAnswerEntrySchema>;

/** The structured answer returned to the agent AND persisted as an interactive_answer sse_event. */
export const InteractiveAnswerSchema = z.discriminatedUnion('kind', [
  z.object({
    blockId: z.string().min(1),
    kind: z.literal('question'),
    status: z.enum(['answered', 'skipped', 'dismissed', 'timedout']),
    entries: z.array(QuestionAnswerEntrySchema),
  }),
  z.object({
    blockId: z.string().min(1),
    kind: z.literal('approval'),
    decision: z.enum(['approved', 'rejected', 'revised', 'dismissed', 'timedout']),
    comment: z.string().max(10_000).optional(),
  }),
]);
export type InteractiveAnswer = z.infer<typeof InteractiveAnswerSchema>;

/**
 * Validate a resolved answer against the block that was actually asked — the check the broker cannot do
 * (it only compares `kind`, and the payload is gone by resolve time). Returns an error message, or null
 * when valid. Catches a hand-crafted answer that settles a block with entries for un-asked questions,
 * labels that aren't options, values that don't match their option's declared machine value, a repeated
 * selection, min/max violations, wrong single-select cardinality, free text on the wrong entry kind, or
 * an "answered" status that does not cover every question. A multi-select's "Other" free text is carried
 * in the distinct `selected.otherText` field (single-select uses a `manual` entry). The UI always offers
 * Other, so free text is always valid on the matching entry kind.
 */
export function validateInteractiveAnswer(
  payload: QuestionBlockPayload | ApprovalPayload,
  answer: InteractiveAnswer,
): string | null {
  if ('questions' in payload) {
    if (answer.kind !== 'question') return 'answer kind does not match a question block';
    // Skip / dismiss / timeout carry no entries; only an answered block does.
    if (answer.status !== 'answered') {
      return answer.entries.length === 0 ? null : `a ${answer.status} answer must not carry entries`;
    }
    const byId = new Map(payload.questions.map((q) => [q.id, q]));
    const seen = new Set<string>();
    for (const entry of answer.entries) {
      const q = byId.get(entry.questionId);
      if (!q) return `answer references unknown question id "${entry.questionId}"`;
      if (seen.has(entry.questionId)) return `duplicate answer for question id "${entry.questionId}"`;
      seen.add(entry.questionId);
      const err = validateAnswerEntry(q, entry);
      if (err) return err;
    }
    // Completeness: exactly one entry per asked question (a skipped step is still an entry).
    if (seen.size !== payload.questions.length) {
      return 'an answered block must have exactly one entry per question';
    }
    return null;
  }
  if (answer.kind !== 'approval') return 'answer kind does not match an approval block';
  // Revise (and its free-text comment) is only valid when the card actually offered it — otherwise a
  // hand-crafted answer could resume the agent on a Revise outcome + free text the UI never showed.
  if (!payload.allowRevise) {
    if (answer.decision === 'revised') return 'approval block did not offer a revise option';
    if (answer.comment != null) return 'approval block did not offer a free-text comment';
  }
  return null;
}

function validateAnswerEntry(q: Question, entry: QuestionAnswerEntry): string | null {
  if (entry.kind === 'skipped') return null;
  if (entry.kind === 'manual') {
    // Single-select Other is a `manual` entry; multi-select carries free text as `otherText`.
    if (q.multiSelect) return `question "${q.id}" did not offer a free-text answer`;
    return entry.text.trim().length > 0 ? null : `question "${q.id}" free-text answer is empty`;
  }
  // selected
  const { labels, values, otherText } = entry;
  if (values.length !== labels.length) {
    return `question "${q.id}" answer values/labels length mismatch`;
  }
  // "Other" free text is carried DISTINCTLY here (never inlined into labels/values), and only a
  // multi-select may offer it. Because it is separate, a value equal to an option label is
  // unambiguous free text and needs no coercion or collision handling.
  if (otherText !== undefined) {
    if (!q.multiSelect) return `question "${q.id}" did not offer a free-text answer`;
    if (otherText.trim().length === 0) return `question "${q.id}" free-text answer is empty`;
  }
  const total = labels.length + (otherText !== undefined ? 1 : 0);
  if (total === 0) return `question "${q.id}" has no selection`;
  if (new Set(labels).size !== labels.length) return `question "${q.id}" repeats a selection`;
  const valueByLabel = new Map(q.options.map((o) => [o.label, o.value]));
  if (!q.multiSelect) {
    if (labels.length !== 1) return `single-select question "${q.id}" must have exactly one selection`;
    const [only] = labels;
    if (only === undefined || !valueByLabel.has(only)) return `question "${q.id}" selection is not one of its options`;
  } else {
    // Every selected label must be a real option — free text lives in otherText, not labels.
    for (const label of labels) {
      if (!valueByLabel.has(label)) return `question "${q.id}" selection includes a label that is not an option`;
    }
    const min = q.min ?? 1;
    if (total < min) return `question "${q.id}" needs at least ${min} selection(s)`;
    if (q.max != null && total > q.max) return `question "${q.id}" allows at most ${q.max} selection(s)`;
  }
  // Every label is a real option now, so its value must be the option's declared machine value. Guarding
  // labels but not values would let a crafted answer pair a real label with an un-offered machine token.
  for (const [i, label] of labels.entries()) {
    if (values[i] !== valueByLabel.get(label)) {
      return `question "${q.id}" answer value does not match its option`;
    }
  }
  return null;
}
